import React, { useEffect, useRef } from "react";
import { Animated, Platform, StyleSheet } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as Haptics from "expo-haptics";
import { useAtom } from "jotai";
import { toastAtom } from "../hooks/useToast";

const TOAST_DURATION_MS = 2500;
const SLIDE_DURATION_MS = 250;

export function ToastOverlay() {
  const [toast, setToast] = useAtom(toastAtom);
  const translateY = useRef(new Animated.Value(-100)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const insets = useSafeAreaInsets();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!toast) return;

    // Haptics
    if (Platform.OS !== "web") {
      if (toast.haptic === "success") {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      } else if (toast.haptic === "error" || toast.haptic === "warning") {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
    }

    // Slide in
    Animated.parallel([
      Animated.timing(translateY, {
        toValue: 0,
        duration: SLIDE_DURATION_MS,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: SLIDE_DURATION_MS,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto-dismiss
    if (hideTimer.current) clearTimeout(hideTimer.current);
    const duration = (toast.duration ?? 2.5) * 1000;
    hideTimer.current = setTimeout(() => {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -100,
          duration: SLIDE_DURATION_MS,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: SLIDE_DURATION_MS,
          useNativeDriver: true,
        }),
      ]).start(() => setToast(null));
    }, duration);

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [toast?.id]);

  if (!toast) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        { top: insets.top + 12 },
        { transform: [{ translateY }], opacity },
      ]}
      pointerEvents="none"
    >
      <Animated.Text style={styles.text}>{toast.title}</Animated.Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 9999,
    backgroundColor: "#fffef6",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "black",
    paddingVertical: 8,
    paddingHorizontal: 20,
    shadowColor: "black",
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.9,
    shadowRadius: 0,
    elevation: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  text: {
    fontFamily: "InterBlack",
    fontSize: 14,
    color: "black",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
